import { useAuth } from '../../lib/auth.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsHeader } from './common.js';

const SUPPORT_EMAIL = 'support@phibian.io';

/**
 * Help & support.
 *
 * Deliberately not a fake ticketing system. Phibian has no in-app support
 * queue, so this page does the two things it honestly can: it routes a person
 * to a real way of reaching a human, and it puts the details a support
 * conversation always asks for in one place so they can be quoted rather than
 * hunted for.
 *
 * The organization identifier is shown here on purpose — it is the one place a
 * raw ID is the useful thing, because support will ask for it. Everywhere else
 * in the product a person or organization is named, not numbered.
 */
export function HelpSupport() {
  const { me } = useAuth();
  const navigate = useNav();

  const subject = encodeURIComponent(`Phibian support — ${me?.orgName ?? 'my organization'}`);

  return (
    <div className="rn-col" style={{ maxWidth: 760 }}>
      <SettingsHeader
        title="Help & support"
        blurb="How to reach us, and the details we’ll ask for when you do."
      />

      <SettingsCard>
        <h2 className="rn-card-title">Get in touch</h2>
        <p style={{ marginTop: 'var(--space-8)' }}>
          Email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}?subject=${subject}`} className="rn-link">
            {SUPPORT_EMAIL}
          </a>{' '}
          and describe what you were doing and what happened. If something looks wrong in your data,
          tell us the person or client involved and roughly when — that is usually enough for us to
          find it.
        </p>
        <p className="rn-muted" style={{ marginBottom: 0 }}>
          Phibian support staff cannot see into your organization’s data by default. Access is
          granted deliberately, is limited to what is needed, expires, and is recorded.
        </p>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Your details</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Quote these if we ask which organization or session you were in.
        </p>
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            gap: 'var(--space-8) var(--space-16)',
            margin: 'var(--space-12) 0 0',
          }}
        >
          <dt className="rn-muted">Organization</dt>
          <dd style={{ margin: 0 }}>{me?.orgName ?? '—'}</dd>
          <dt className="rn-muted">Organization ID</dt>
          <dd style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>{me?.orgId ?? '—'}</dd>
          <dt className="rn-muted">Signed in as</dt>
          <dd style={{ margin: 0 }}>{me?.name?.trim() || me?.email || '—'}</dd>
          <dt className="rn-muted">Your roles</dt>
          <dd style={{ margin: 0 }}>{me?.roles?.length ? me.roles.join(' · ') : '—'}</dd>
        </dl>
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Common places to look first</h2>
        <ul
          style={{
            margin: 'var(--space-10) 0 0',
            paddingLeft: '1.1rem',
            display: 'grid',
            gap: 'var(--space-8)',
          }}
        >
          <li>
            Someone can’t see a screen —{' '}
            <a
              href="/settings/people"
              className="rn-link"
              onClick={(e) => {
                e.preventDefault();
                navigate('/settings/people');
              }}
            >
              People &amp; roles
            </a>{' '}
            shows what each person’s roles and direct permissions actually grant.
          </li>
          <li>
            Alerts arriving late or not at all —{' '}
            <a
              href="/settings/rules"
              className="rn-link"
              onClick={(e) => {
                e.preventDefault();
                navigate('/settings/rules');
              }}
            >
              Automations
            </a>{' '}
            shows whether a rule is active, when it last ran, and what it did.
          </li>
          <li>
            Clock-ins flagged unexpectedly —{' '}
            <a
              href="/settings/location"
              className="rn-link"
              onClick={(e) => {
                e.preventDefault();
                navigate('/settings/location');
              }}
            >
              Location &amp; geofences
            </a>{' '}
            shows the policy in force and the sites it checks against.
          </li>
        </ul>
      </SettingsCard>
    </div>
  );
}
